import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { getSupabase, isSupabaseConfigured } from '../lib/supabaseClient';

/**
 * Subscription state for the signed-in user.
 *
 * Reads `profiles.has_active_subscription` + `profiles.subscription_metadata`
 * (a JSONB column the workspace writes after a successful checkout). When
 * `subscription_metadata.expires_at` has passed we treat the subscription as
 * lapsed even if `has_active_subscription` is still true on the row, gives
 * us correct gating without needing a cron job to flip the flag.
 *
 * Exposes a `markPremium(planKey, periodDays)` action used by the billing
 * success page; it writes both flags + metadata to the user's own profile
 * (allowed by RLS) and refreshes local state.
 */
export const useSubscription = () => {
  const { user } = useAuth();
  const userId = user?.id;

  const [isPremium, setIsPremium] = useState(false);
  const [metadata, setMetadata] = useState({});
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!userId || !isSupabaseConfigured()) {
      setIsPremium(false);
      setMetadata({});
      setLoading(false);
      return;
    }
    try {
      const supabase = getSupabase();
      const { data, error } = await supabase
        .from('profiles')
        .select('has_active_subscription, subscription_metadata, subscription_updated_at')
        .eq('id', userId)
        .maybeSingle();
      if (error) throw error;
      const md = data?.subscription_metadata || {};
      const expiresAt = md?.expires_at ? new Date(md.expires_at) : null;
      const stillActive = data?.has_active_subscription
        && (!expiresAt || expiresAt.getTime() > Date.now());
      setIsPremium(Boolean(stillActive));
      setMetadata(md);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[subscription] read failed:', e?.message);
      setIsPremium(false);
      setMetadata({});
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    setLoading(true);
    refresh();
  }, [refresh]);

  const markPremium = useCallback(async (planKey, periodDays, sessionId, productId) => {
    if (!userId || !isSupabaseConfigured()) return false;
    const nowIso = new Date().toISOString();
    const expiresAt = new Date(Date.now() + (Number(periodDays) || 30) * 24 * 60 * 60 * 1000).toISOString();
    // Match the shape the mobile app already writes onto subscription_metadata
    // (we observed: plan / source / expires_at / product_id are the fields
    // mobile-app admin-grant + client_restore rows use). `platform: "web"`
    // tells future code that this row was activated via the workspace.
    const newMetadata = {
      source: 'workspace_web_stripe',
      platform: 'web',
      plan: planKey,
      product_id: productId || (planKey === 'weekly' ? 'com.buildpilot.premiumweekly' : 'com.buildpilot.premium'),
      activated_at: nowIso,
      expires_at: expiresAt,
      stripe_session_id: sessionId,
      period_days: Number(periodDays) || 30,
    };
    try {
      const supabase = getSupabase();
      const { error } = await supabase.from('profiles').upsert({
        id: userId,
        has_active_subscription: true,
        subscription_metadata: newMetadata,
        subscription_updated_at: nowIso,
        updated_at: nowIso,
      }, { onConflict: 'id' });
      if (error) throw error;
      setIsPremium(true);
      setMetadata(newMetadata);
      return true;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[subscription] markPremium failed:', e?.message);
      return false;
    }
  }, [userId]);

  return { loading, isPremium, metadata, refresh, markPremium };
};
